import { useCallback, useEffect, useMemo, useState } from 'react';

import { useOktaAuth } from '@okta/okta-react';
import {
  type MarkConsentRequest,
  PatientConsentStatus,
  createPatientConsentRecord,
} from '@repo/videa-notes';

import configurations from '@/config';

/**
 * Safely converts unifiedAppointmentId to a number or undefined.
 * Returns undefined if the value is undefined, null, empty string, or NaN.
 */
const safeUnifiedAppointmentId = (
  unifiedAppointmentId: string | undefined,
): number | undefined => {
  if (!unifiedAppointmentId || unifiedAppointmentId.trim() === '') {
    return undefined;
  }

  const num = Number(unifiedAppointmentId);
  return isNaN(num) ? undefined : num;
};

interface PatientInfo {
  firstName?: string;
  lastName?: string;
  dob?: string;
}

interface UsePatientConsentProps {
  patient: PatientInfo | undefined;
  patientNameForConsentRecord: string;
  appointmentStartsAt: string | undefined;
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  currentOrganizationId: string;
  currentPracticeId: string;
  username: string;
  enableVoiceNotesConsent: boolean;
  onStartRecordingSuccess?: () => void;
  onStartProviderOnlyRecordingSuccess?: () => void;
  onPatientConsentDeniedSuccess?: () => void;
}

interface UsePatientConsentReturn {
  patientConsentRecordId: string | undefined;
  handleStartRecording: () => Promise<void>;
  handleStartProviderOnlyRecording: () => Promise<void>;
  handlePatientConsentDenied: () => Promise<void>;
}

export const usePatientConsent = ({
  patient,
  patientNameForConsentRecord,
  appointmentStartsAt,
  unifiedAppointmentId,
  standalonePatientVisitId,
  currentOrganizationId,
  currentPracticeId,
  username,
  enableVoiceNotesConsent,
  onStartRecordingSuccess,
  onStartProviderOnlyRecordingSuccess,
  onPatientConsentDeniedSuccess,
}: UsePatientConsentProps): UsePatientConsentReturn => {
  const { oktaAuth } = useOktaAuth();
  const [patientConsentRecordId, setPatientConsentRecordId] = useState<
    string | undefined
  >(undefined);

  const uploadDeps = useMemo(
    () => ({
      getAccessToken: () => oktaAuth.getAccessToken() || null,
      apiUrl: configurations.API_URL,
    }),
    [oktaAuth],
  );

  /**
   * Handle recording start when patient consent is given (YES)
   */
  const handleStartRecording = useCallback(async () => {
    try {
      if (enableVoiceNotesConsent) {
        const consentRequest: MarkConsentRequest = {
          consentStatus: PatientConsentStatus.YES,
          isGivenByPatient: true,
          patientFullName: patientNameForConsentRecord,
          patientAppointmentAt: appointmentStartsAt ?? '',
          recordedBy: username,
          patientDob: patient?.dob,
        };

        const response = await createPatientConsentRecord({
          consentRequest,
          unifiedAppointmentId: safeUnifiedAppointmentId(unifiedAppointmentId),
          standalonePatientVisitId: standalonePatientVisitId,
          currentOrganizationId,
          currentPracticeId,
          deps: uploadDeps,
        });
        setPatientConsentRecordId(response.id);
      }
      onStartRecordingSuccess?.();
    } catch (error) {
      console.error(error);
    }
  }, [
    enableVoiceNotesConsent,
    patientNameForConsentRecord,
    appointmentStartsAt,
    username,
    patient?.dob,
    unifiedAppointmentId,
    standalonePatientVisitId,
    currentOrganizationId,
    currentPracticeId,
    uploadDeps,
    onStartRecordingSuccess,
  ]);

  /**
   * Handle recording start for provider-only recording (NOT_REQUIRED consent status)
   */
  const handleStartProviderOnlyRecording = useCallback(async () => {
    try {
      const consentRequest: MarkConsentRequest = {
        consentStatus: PatientConsentStatus.NOT_REQUIRED,
        isGivenByPatient: true,
        patientFullName: patientNameForConsentRecord,
        patientAppointmentAt: appointmentStartsAt ?? '',
        recordedBy: username,
        patientDob: patient?.dob,
      };

      const response = await createPatientConsentRecord({
        consentRequest,
        unifiedAppointmentId: safeUnifiedAppointmentId(unifiedAppointmentId),
        standalonePatientVisitId: standalonePatientVisitId,
        currentOrganizationId,
        currentPracticeId,
        deps: uploadDeps,
      });
      setPatientConsentRecordId(response.id);
      onStartProviderOnlyRecordingSuccess?.();
    } catch (error) {
      console.error(error);
    }
  }, [
    patientNameForConsentRecord,
    appointmentStartsAt,
    username,
    patient?.dob,
    unifiedAppointmentId,
    standalonePatientVisitId,
    currentOrganizationId,
    currentPracticeId,
    uploadDeps,
    onStartProviderOnlyRecordingSuccess,
  ]);

  /**
   * Handle when patient explicitly denies consent (NO)
   */
  const handlePatientConsentDenied = useCallback(async () => {
    try {
      const consentRequest: MarkConsentRequest = {
        consentStatus: PatientConsentStatus.NO,
        isGivenByPatient: true,
        patientFullName: patientNameForConsentRecord,
        patientAppointmentAt: appointmentStartsAt ?? '',
        recordedBy: username,
        patientDob: patient?.dob,
      };

      await createPatientConsentRecord({
        consentRequest,
        unifiedAppointmentId: safeUnifiedAppointmentId(unifiedAppointmentId),
        standalonePatientVisitId: standalonePatientVisitId,
        currentOrganizationId,
        currentPracticeId,
        deps: uploadDeps,
      });
      onPatientConsentDeniedSuccess?.();
    } catch (error) {
      console.error(error);
    }
  }, [
    patientNameForConsentRecord,
    appointmentStartsAt,
    username,
    patient?.dob,
    unifiedAppointmentId,
    standalonePatientVisitId,
    currentOrganizationId,
    currentPracticeId,
    uploadDeps,
    onPatientConsentDeniedSuccess,
  ]);

  // Reset consent record ID when appointment changes to prevent stale consent
  // from a previous patient being associated with a new patient's audio upload
  useEffect(() => {
    setPatientConsentRecordId(undefined);
  }, [unifiedAppointmentId, standalonePatientVisitId]);

  return {
    patientConsentRecordId,
    handleStartRecording,
    handleStartProviderOnlyRecording,
    handlePatientConsentDenied,
  };
};
